import {
  calculateWizardState,
  getProgressForRoute,
  getWizardNavigationState,
  getWizardRoutes,
  getWizardSectionRoutes,
  IndexedPage,
  indexedPageById,
  matchesCondition,
  pageById,
  questionnaireResolveExpressionValue,
  selectableCategoriesInGroups,
  WizardNavigationState,
  WizardRoute
} from '@features/formation-viewer/formation-wizard/model'
import { SDKMessage, SDKResponse } from '@features/sdk-module'
import {
  FolderRequest,
  FolderUIRole,
  selFolderRole,
  selResolvedInputs,
  stFolderModule,
  STFolderModuleState
} from '@features/st-folder-viewer/st-folder-module'
import { defineModule } from '@st/redux'
import {
  Organization,
  STChecklistItem,
  STDocument,
  SubmitClientOrganizerValidationResult
} from '@st/sdk'
import {
  categorySelectInput,
  QuestionnaireCategoryOption,
  WizardCategoryConfig,
  WizardConfig
} from '@st/ui-config'
import { isEmpty, JsonMap } from '@st/util/json-value'
import { removeValues } from '@st/util/object'
import { Progress } from '@st/util/progress'
import { match } from 'ts-pattern'
import {
  formatValidationErrors,
  QuestionnairePageValidationResult,
  validateQuestionnairePage
} from './st-questionnaire-validation'

export type QuestionnaireModuleState = {
  folderId: string
  wizardConfig: WizardConfig
  organization: Organization
  preview: boolean
  selectedMode: 'questionnaire' | 'dashboard'
  questionnaire: QuestionnaireState
  dashboard: QuestionnaireDashboardState
}

export type QuestionnaireState = {
  route: WizardRoute
  validationResult: QuestionnairePageValidationResult
}

export type QuestionnaireDashboardState =
  | { submitDialog: undefined }
  | { submitDialog: { status: 'loading' } }
  | {
      submitDialog: {
        status: 'open'
        validationResult: SubmitClientOrganizerValidationResult
        submissionCount: number
      }
    }
  | {
      submitDialog: {
        status: 'submitting'
        validationResult: SubmitClientOrganizerValidationResult
        submissionCount: number
      }
    }

export type QuestionnaireDashboardMessage =
  | { type: 'openSubmitDialog' }
  | { type: 'submitClientOrganizer'; forceSubmit: boolean }
  | { type: 'closeSubmitDialog' }

export type STQuestionnaireMessage =
  | QuestionnaireDashboardMessage
  | { type: 'enterQuestionnaire' }
  | { type: 'navigateNext' }
  | { type: 'navigatePrev' }
  | { type: 'navigate'; route: WizardRoute; autoSelectCategoryIfMissing?: boolean }
  | { type: 'returnToDashboard' }
  | { type: 'deleteDocument'; docId: string }
  | { type: 'rerunValidation' }
  | SDKResponse

type STQuestionnaireInit = {
  folderId: string
  wizardConfig: WizardConfig
  organization: Organization
  preview: boolean
}

type STQuestionnaireDeps = { stFolder: typeof stFolderModule }

type STQuestionnaireSend = {
  sdk: SDKMessage
  stFolder: { type: 'request'; request: FolderRequest }
}

export type { WizardNavigationState }

export const stQuestionnaireModule = defineModule<
  QuestionnaireModuleState,
  STQuestionnaireMessage,
  STQuestionnaireInit,
  STQuestionnaireSend,
  STQuestionnaireDeps
>({
  name: 'stQuestionnaire',
  deps: { stFolder: stFolderModule },
  init: (state, _context, deps) => {
    return {
      folderId: state.folderId,
      wizardConfig: state.wizardConfig,
      organization: state.organization,
      mode: 'dashboard',
      route: undefined,
      validationResult: { ok: true },
      preview: state.preview,
      selectedMode: 'dashboard',
      dashboard: { submitDialog: undefined },
      questionnaire: {
        route: selInitialQuestionnaireRoute({
          lastSavedPageId:
            deps.stFolder.folderState?.folder.clientOrganizer.questionnaireLastPageId,
          inputs: selResolvedInputs(deps.stFolder),
          priorInputs: deps.stFolder.folderState?.prevInputs ?? {},
          folderRole: selFolderRole(deps.stFolder),
          wizardConfig: state.wizardConfig
        }),
        validationResult: { ok: true }
      }
    }
  },
  handle: (state, message, _context, deps) => {
    const folder = deps.stFolder.folderState!.folder
    const folderRole = selFolderRole(deps.stFolder)

    return match<
      { state: QuestionnaireModuleState; message: STQuestionnaireMessage },
      QuestionnaireModuleState | [QuestionnaireModuleState, Partial<STQuestionnaireSend>]
    >({ state, message })
      .with(
        { state: { selectedMode: 'dashboard' }, message: { type: 'enterQuestionnaire' } },
        ({ state }) => {
          return {
            ...state,
            selectedMode: 'questionnaire'
          }
        }
      )
      .with({ message: { type: 'navigatePrev' } }, ({ state, message }) => {
        const navState = selQuestionnaireNavState(state, deps)
        const route = navState!.prev!
        const newState: QuestionnaireModuleState = {
          ...state,
          questionnaire: { route, validationResult: { ok: true } }
        }
        return [
          newState,
          // we don't want to save the client's questionnaire progress when the accountant is previewing it
          folderRole == 'client'
            ? {
                sdk: {
                  type: 'request',
                  request: {
                    type: 'folders/saveQuestionnaireProgress',
                    pageId: route.pageId,
                    folderId: folder.id,
                    progress: Progress.toNumber(selQuestionnaireProgress(newState, deps))
                  }
                }
              }
            : {}
        ] as [QuestionnaireModuleState, STQuestionnaireSend]
      })
      .with(
        { state: { selectedMode: 'questionnaire' }, message: { type: 'navigateNext' } },
        ({ state }) => {
          var navState = selQuestionnaireNavState(state, deps)
          var curPage = selQuestionnaireCurrentPage(state, deps)
          var inputs = selResolvedInputs(deps.stFolder)
          var validationResult = validateQuestionnairePage(
            selQuestionnaireCategoryOptions(state.wizardConfig),
            curPage?.components ?? [],
            inputs
          )
          if (!validationResult.ok) {
            return { ...state, questionnaire: { ...state.questionnaire, validationResult } }
          }
          const route = navState!.next!
          const newState: QuestionnaireModuleState = {
            ...state,
            questionnaire: { route, validationResult }
          }
          return [
            newState,
            // we don't want to save the client's questionnaire progress when the accountant is previewing it
            folderRole == 'client'
              ? {
                  sdk: {
                    type: 'request',
                    request: {
                      type: 'folders/saveQuestionnaireProgress',
                      pageId: route.pageId,
                      folderId: folder.id,
                      progress: Progress.toNumber(selQuestionnaireProgress(newState, deps))
                    }
                  }
                }
              : {}
          ] as [QuestionnaireModuleState, STQuestionnaireSend]
        }
      )
      .with(
        { state: { selectedMode: 'questionnaire' }, message: { type: 'navigate' } },
        ({ state, message }) => {
          const route = message.route
          const newState: QuestionnaireModuleState = {
            ...state,
            questionnaire: { route, validationResult: { ok: true } }
          }
          const categoryInputKey = route.categoryId
            ? categorySelectInput(route.categoryId)
            : undefined
          // In some cases (like when you click on a shortcut to jump to a section)
          // we want to auto-select the category if this is missing
          //
          // The questionnaire dynamically filters out unselected categories so the user would end up
          // in a black hole if we jump to an unselected category.
          const shouldSelectCategory =
            message.autoSelectCategoryIfMissing &&
            categoryInputKey &&
            !deps.stFolder.folderState!.inputs[categoryInputKey]
          const folderRequest: { type: 'request'; request: FolderRequest } | undefined =
            shouldSelectCategory
              ? {
                  type: 'request',
                  request: {
                    type: 'folders/setFolderInputs',
                    folderId: folder.id,
                    inputs: { [categoryInputKey]: true }
                  }
                }
              : undefined
          if (message.route && folderRole == 'client') {
            return [
              newState,
              removeValues(
                {
                  sdk: {
                    type: 'request',
                    request: {
                      type: 'folders/saveQuestionnaireProgress',
                      pageId: route.pageId,
                      folderId: folder.id,
                      progress: Progress.toNumber(selQuestionnaireProgress(newState, deps))
                    }
                  },
                  stFolder: folderRequest
                },
                // drop undefined to avoid stFolder sending undefined
                undefined
              )
            ]
          }
          return [newState, removeValues({ stFolder: folderRequest }, undefined)]
        }
      )
      .with(
        { state: { selectedMode: 'questionnaire' }, message: { type: 'returnToDashboard' } },
        () => {
          return { ...state, selectedMode: 'dashboard', dashboard: { submitDialog: undefined } }
        }
      )
      .with(
        { state: { selectedMode: 'questionnaire' }, message: { type: 'rerunValidation' } },
        ({ state }) => {
          const categoryOptions = selQuestionnaireCategoryOptions(state.wizardConfig)
          var curPage = selQuestionnaireCurrentPage(state, deps)
          var validationResult = validateQuestionnairePage(
            categoryOptions,
            curPage?.components ?? [],
            selResolvedInputs(deps.stFolder)
          )
          return {
            ...state,
            questionnaire: { ...state.questionnaire, validationResult }
          }
        }
      )
      .with(
        { state: { selectedMode: 'dashboard' }, message: { type: 'openSubmitDialog' } },
        ({ state, message }) => {
          return [
            { ...state, dashboard: { submitDialog: { status: 'loading' } } },
            {
              sdk: {
                type: 'request',
                request: {
                  type: 'folders/validateSubmitClientOrganizer',
                  folderId: state.folderId
                }
              }
            }
          ]
        }
      )
      .with(
        {
          state: { selectedMode: 'dashboard', dashboard: { submitDialog: { status: 'open' } } },
          message: {
            type: 'response',
            operation: { type: 'folders/validateSubmitClientOrganizer' }
          }
        },
        ({ state, message }) => {
          // Ensure submitDialog has the expected shape for 'open' status
          const currentOpenDialog = state.dashboard.submitDialog as Extract<
            NonNullable<QuestionnaireModuleState['dashboard']['submitDialog']>,
            { status: 'open' }
          >
          return [
            {
              ...state,
              dashboard: {
                submitDialog: {
                  status: 'open',
                  validationResult: message.operation.response,
                  submissionCount: currentOpenDialog.submissionCount
                }
              }
            },
            {
              sdk: {
                type: 'request',
                request: {
                  type: 'folders/validateSubmitClientOrganizer',
                  folderId: state.folderId,
                  validations: ['document_type_checklist_items', 'category_checklist_items']
                }
              }
            }
          ]
        }
      )
      .with(
        {
          state: { selectedMode: 'dashboard', dashboard: { submitDialog: { status: 'loading' } } },
          message: {
            type: 'response',
            operation: { type: 'folders/validateSubmitClientOrganizer' }
          }
        },
        ({ state, message }) => {
          return {
            ...state,
            dashboard: {
              submitDialog: {
                status: 'open',
                validationResult: message.operation.response,
                submissionCount: 0
              }
            }
          }
        }
      )
      .with(
        {
          state: {
            selectedMode: 'dashboard',
            dashboard: { submitDialog: { status: 'open' } }
          },
          message: { type: 'submitClientOrganizer' }
        },
        ({ state, message }) => {
          // Ensure submitDialog has the expected shape for 'open' status
          const currentOpenDialog = state.dashboard.submitDialog as Extract<
            NonNullable<QuestionnaireModuleState['dashboard']['submitDialog']>,
            { status: 'open' }
          >

          return [
            {
              ...state,
              dashboard: {
                submitDialog: {
                  status: 'submitting',
                  validationResult: currentOpenDialog.validationResult,
                  submissionCount: currentOpenDialog.submissionCount + 1
                }
              }
            },
            {
              sdk: {
                type: 'request',
                delay: 1000,
                request: {
                  type: 'folders/submitClientOrganizer',
                  folderId: folder.id,
                  forceSubmit: message.forceSubmit
                }
              }
            }
          ]
        }
      )
      .with(
        {
          state: {
            selectedMode: 'dashboard',
            dashboard: { submitDialog: { status: 'submitting' } }
          },
          message: { type: 'response', operation: { type: 'folders/submitClientOrganizer' } }
        },
        ({ state, message }) => {
          if (message.operation.response.validationResult!.status == 'missing_reasons_error') {
            return {
              ...state,
              dashboard: {
                submitDialog: {
                  status: 'open',
                  validationResult: message.operation.response.validationResult!,
                  submissionCount: state.dashboard.submitDialog.submissionCount
                }
              }
            }
          }
          return { ...state, dashboard: { submitDialog: undefined } }
        }
      )
      .with(
        {
          state: { selectedMode: 'dashboard', dashboard: { submitDialog: { status: 'open' } } },
          message: { type: 'closeSubmitDialog' }
        },
        () => {
          return { ...state, dashboard: { submitDialog: undefined } }
        }
      )
      .with({ message: { type: 'deleteDocument' } }, ({ state, message }) => {
        return [
          state,
          {
            sdk: {
              type: 'request',
              request: {
                type: 'folders/deleteDocument',
                folderId: folder.id,
                documentId: message.docId
              }
            }
          }
        ]
      })
      .otherwise(() => state)
  }
})

export function selQuestionnaireValidationErrorMessage(state: QuestionnaireModuleState) {
  return match(state)
    .with({ questionnaire: { validationResult: { ok: false } } }, ({ questionnaire }) => {
      return formatValidationErrors(questionnaire.validationResult.errors)
    })
    .otherwise(() => undefined)
}

export function hasValidationError(
  validationResult: QuestionnairePageValidationResult,
  inputKey: string
) {
  if (validationResult.ok) {
    return false
  }
  return validationResult.errors.some((e) => e.keys.includes(inputKey))
}

function selInitialQuestionnaireRoute(arg: {
  inputs: JsonMap
  priorInputs: JsonMap
  folderRole: FolderUIRole
  wizardConfig: WizardConfig
  lastSavedPageId: string | undefined
}): WizardRoute {
  const wizardState = selQuestionnaireState({
    inputs: arg.inputs,
    priorInputs: arg.priorInputs,
    folderRole: arg.folderRole,
    wizardConfig: arg.wizardConfig
  })

  const lastSavedPage = arg.lastSavedPageId
    ? indexedPageById(wizardState, arg.lastSavedPageId)
    : undefined

  if (lastSavedPage) {
    return routeForIndexedPage(lastSavedPage)
  }

  return routeForIndexedPage(wizardState.filteredPages[0])
}

type QuestionnaireProgress = {
  lastPageId: string | undefined
  progress: number
}

export function selQuestionnaireSavedProgress(
  questionnaireState: QuestionnaireModuleState,
  deps: { stFolder: STFolderModuleState }
): QuestionnaireProgress {
  return {
    lastPageId: deps.stFolder.folderState?.folder.clientOrganizer.questionnaireLastPageId,
    progress: deps.stFolder.folderState?.folder.clientOrganizer.questionnaireProgress ?? 0
  }
}

export function selQuestionnaireCurrentPage(
  state: QuestionnaireModuleState,
  deps: { stFolder: STFolderModuleState }
) {
  const inputs = selResolvedInputs(deps.stFolder)
  const folderRole = selFolderRole(deps.stFolder)
  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs: deps.stFolder.folderState?.prevInputs ?? {},
    folderRole,
    wizardConfig: state.wizardConfig
  })

  return pageById(wizardState, state.questionnaire.route!.pageId)
}

export function selQuestionnaireNavState(
  state: QuestionnaireModuleState,
  deps: { stFolder: STFolderModuleState }
) {
  const inputs = selResolvedInputs(deps.stFolder)
  const folderRole = selFolderRole(deps.stFolder)
  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs: deps.stFolder.folderState?.prevInputs ?? {},
    folderRole,
    wizardConfig: state.wizardConfig
  })
  const routes = getWizardRoutes(wizardState)

  return getWizardNavigationState(routes, state.questionnaire.route!)
}

export function selQuestionnaireProgress(
  state: QuestionnaireModuleState,
  deps: { stFolder: STFolderModuleState }
): Progress {
  const inputs = selResolvedInputs(deps.stFolder)
  const folderRole = selFolderRole(deps.stFolder)
  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs: deps.stFolder.folderState?.prevInputs ?? {},
    folderRole,
    wizardConfig: state.wizardConfig
  })
  return getProgressForRoute(wizardState, state.questionnaire.route!.pageId)
}

export type WizardSectionTab = { label: string; route: WizardRoute }
export function selQuestionnaireTabs(
  state: QuestionnaireModuleState,
  deps: { stFolder: STFolderModuleState }
): WizardSectionTab[] {
  const inputs = selResolvedInputs(deps.stFolder)
  const priorInputs = deps.stFolder.folderState?.prevInputs ?? {}
  const folderRole = selFolderRole(deps.stFolder)

  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs,
    folderRole,
    wizardConfig: state.wizardConfig
  })

  const routes = getWizardRoutes(wizardState)
  return getWizardSectionRoutes(routes).map((route) => {
    const section = state.wizardConfig.sections.find((s) => s.id == route.sectionId)
    return {
      label: section ? section.name : '<missing>',
      route: route
    }
  })
}

export function selQuestionnairePersonalizedNote(
  state: QuestionnaireModuleState,
  deps: { stFolder: STFolderModuleState }
) {
  const pageId = state.questionnaire.route?.pageId
  if (!pageId) {
    return undefined
  }
  return deps.stFolder.folderState!.personalizedNotes.find((n) => n.pageId == pageId)
}

export function selQuestionnaireShouldShowSubtitle(
  state: QuestionnaireModuleState,
  deps: { stFolder: STFolderModuleState }
) {
  const page = selQuestionnaireCurrentPage(state, deps)
  const subtitleShowWhen = page?.subtitleShowWhen ?? undefined

  if (isEmpty(subtitleShowWhen)) {
    return true
  }

  const conditionResult = matchesCondition(subtitleShowWhen!, {
    resolveValue: (path) =>
      questionnaireResolveExpressionValue(path, {
        showWhenExpr: subtitleShowWhen,
        inputs: deps.stFolder.folderState!.inputs,
        priorInputs: deps.stFolder.folderState!.prevInputs,
        context: {}
      })
  })

  return conditionResult
}

export function selQuestionnaireRoute(state: QuestionnaireModuleState): WizardRoute | undefined {
  return state.questionnaire.route
}

export function categorySelectorCategoryGroups(state: QuestionnaireModuleState, sectionId: string) {
  return selectableCategoriesInGroups(getCategoriesForSection(state.wizardConfig, sectionId))
}

function routeForIndexedPage(indexedPage: IndexedPage) {
  return {
    pageId: indexedPage.page.id,
    sectionId: indexedPage.section.id,
    categoryId: indexedPage.category?.id
  }
}

function selQuestionnaireState(arg: {
  inputs: JsonMap
  priorInputs: JsonMap
  wizardConfig: WizardConfig
  folderRole: FolderUIRole
}) {
  const inputs = arg.inputs
  const context = { role: arg.folderRole }
  return calculateWizardState({
    inputs,
    priorInputs: arg.priorInputs,
    uiConfig: arg.wizardConfig,
    context: context
  })
}

export type DocumentStackItem = {
  doc: STDocument
  checklistItemCandidates: STChecklistItem[]
}

export function selQuestionnaireCategoryOptions(
  uiConfig: WizardConfig
): QuestionnaireCategoryOption[] {
  const categoryOptions: QuestionnaireCategoryOption[] = []
  for (const section of uiConfig.sections) {
    for (const category of section.categories) {
      categoryOptions.push({
        sectionId: section.id,
        label: category.name,
        selectedUserInputKey: category.isSelectedUserInputKey!
      })
    }
  }
  return categoryOptions
}

function getCategoriesForSection(
  uiConfig: WizardConfig,
  sectionId: string
): WizardCategoryConfig[] {
  const section = uiConfig.sections.find((el) => el.id == sectionId)
  if (section == null) {
    return []
  }
  return section.categories
}
